/**
 * CUSTOMIZATION: shared resolution of a dotCMS contentlet image field into an `<img>` source,
 * so the banner, image and tooltip content types cannot drift apart.
 *
 * dotCMS returns `idPath` with `?language_id=1` already attached, and transform segments
 * appended after a query string are silently ignored (HTTP 200, untransformed original), so the
 * query string is stripped here; the image loader re-adds `language_id` after the transform.
 * `width`/`height` come from the GraphQL `Binary` type only when `idPath` does, so an unset
 * device variant yields all three as null together.
 */
export interface DotCmsImageSource {
  /** Host-relative `/dA/…` path, free of any query string. */
  path: string;
  /** Intrinsic pixel size, when dotCMS knows it. Absent for an unset variant. */
  width?: number;
  height?: number;
}

/** Strips any query string; `/dA/x/img.jpg?language_id=1` -> `/dA/x/img.jpg`. */
export function stripQueryString(url: string): string {
  return url ? url.split('?')[0] : '';
}

/**
 * Resolves one image field of a contentlet into a clean path plus its intrinsic size.
 *
 * `imageKey` is the field name already resolved for the current breakpoint — the fallback order
 * differs per content type, so choosing it stays with the caller. Returns `undefined` when the
 * field holds no usable asset, so callers never hand an empty value to `ngSrc`.
 */
export function resolveDotCmsImageSource(
  contentlet: Record<string, unknown>,
  imageKey: string,
  fallbackInode?: string,
  assetPathOffset = 7
): DotCmsImageSource | undefined {
  const field = contentlet?.[imageKey] as { idPath?: unknown; width?: number; height?: number } | undefined;
  const idPath = typeof field?.idPath === 'string' ? stripQueryString(field.idPath) : '';
  const { width, height } = field ?? {};

  if (idPath.startsWith('/dA/')) {
    return { path: idPath, width, height };
  }

  // Rebuild from the inode. Must also fire when `idPath` is empty, not only when it holds a
  // non-/dA/ path: dotCMS GraphQL returns a null binary for FILEASSET-based content types, and
  // `/dA/<inode>/` still resolves to the default binary.
  if (fallbackInode) {
    const relativePath = idPath ? idPath.split('/').slice(assetPathOffset).join('/') : '';
    return { path: `/dA/${fallbackInode}/${relativePath}`, width, height };
  }

  return idPath ? { path: idPath, width, height } : undefined;
}
